import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Link,
  ThemeProvider,
  Toolbar,
  createTheme,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../shared/hooks/sharedHooks";
import Loader from "../../../shared/components/Loader";
import { FileModel, NotificationMessage } from "../../../shared/models/models";
import { ChangeStockItemModel, CustomerInventoryState } from "../models/models";
import { setError, setStockItemsUploadResult, setSuccess, uploadStockItems } from "../customerInventorySlice";
import { useNavigate, useParams } from "react-router-dom";
import { SubmitHandler, useForm } from "react-hook-form";
import UploadFile from "../../../shared/components/UploadFile";
import ProductBatchResultDialog from "../../../admin/components/catalog/ProductBatchResultDialog";
import useTranslation from '../../../shared/hooks/useTranslation';

const UploadStockItemsFile = () => {
  const { stockItem, stockItemsUploadResult, loading, error } = useAppSelector<CustomerInventoryState>(
    (state: any) => state.customerInventory);
  const dispatch = useAppDispatch();

  const { handleSubmit } = useForm<ChangeStockItemModel>();

  const navigate = useNavigate();

  const { id } = useParams();
  if (!id) {
    navigate(`/inventory`);
  }

  const [file, setFile] = useState<FileModel | null>(stockItem?.file || null);

  const handlerCreateFile = (file: FileModel | null) => {
    setFile(file);
  };

  const onSubmit: SubmitHandler<any> = () => {
    handleUploadFile();
  };

  const handleUploadFile = async () => {
    if (!file) {
      return;
    }

    dispatch(uploadStockItems({ groupId: id!!, file: file }));
  };

  const handleCancel = () => {
    navigate("/supplier");
  };

  const handleCloseUploadResult = () => {
    dispatch(setStockItemsUploadResult(undefined));

    if (error) {
      dispatch(setError(undefined));
      return;
    }

    navigate("/supplier");
  };

  const init = async () => {
  };

  useEffect(() => {
    init();
  }, []);

  const { translate: tInventory } = useTranslation('inventory');
  const { translate: tCommon } = useTranslation('common');

  const csvColumns = [
    tCommon('brand'),
    tInventory('category'),
    tInventory('productNumber'),
    tInventory('alternativeNumber'),
    tCommon('condition'),
    tInventory('availability'),
    tInventory('price'),
    tInventory('currency', { defaultValue: 'Currency' }),
    tInventory('quantity'),
    tInventory('unitsOfMeasurement', { defaultValue: 'Units of measurement' }),
    tInventory('weightKg'),
    tInventory('heightMm'),
    tInventory('widthMm'),
    tInventory('depthMm'),
    tCommon('description'),
    tInventory('imageUrl', { defaultValue: 'Image URL' })
  ];

  const theme = createTheme({
    components: {
      MuiToolbar: {
        styleOverrides: {
          root: {
            background: "#fff",
            borderBottom: "1px solid #d5d6d9",
          },
        },
      },
      MuiInputBase: {
        styleOverrides: {
          root: {
            borderRadius: "6px 0 0 6px !important",
          },
        },
      },
    },
  });

  if (loading) {
    return <Loader />;
  }

  return (
    <ThemeProvider theme={theme}>
      <Toolbar className="flex items-center justify-between w-full">
        <div className="flex items-center">
          <p className="heading-2">{tInventory('excelFileUpload', { defaultValue: 'Excel file upload' })}</p>
        </div>
        <div className="flex"></div>
      </Toolbar>

      <Box className="grid grid-cols-4 gap-5 p-5">
        <Box className="col-span-2">
          <form id="uploadFileForm" onSubmit={handleSubmit(onSubmit)}>
            <div>
              <UploadFile file={file} variant="default" onChangeFile={handlerCreateFile} />
            </div>

            <div className="mt-4 flex justify-center">
              <Link
                href="/assets/inventory.csv"
                target="_blank"
                sx={{ textDecoration: "none", fontSize: "14px", fontWeight: "medium", lineHeight: "28px" }}>
                {tInventory('downloadTemplateCsv', { defaultValue: 'Download template in CSV' })}
              </Link>
            </div>

            <div className="mt-6">
              <p className="text-14-regular" style={{ color: "#000" }}>
                {tInventory('csvUploadInstruction', { defaultValue: 'The easiest way to create a CSV file for a batch upload is to put your data into a spreadsheet and then export it to a .csv file.' })}
              </p>
            </div>

            <div className="mt-4 text-14-regular" style={{ color: "#414C58" }}>
              <p>
                {tInventory('csvNoHeadersInstruction', { defaultValue: 'You don’t need headers, just put in data about your product data in the given order (columns left to right):' })}
              </p>
              <ul style={{ listStyleType: "disc", listStylePosition: "inside" }}>
                {csvColumns.map((column, index) => (<li key={`csv-column-${index}`}>{column}</li>))}
              </ul>
            </div>
          </form>
        </Box>

        <Box>
          <Button form="uploadFileForm" type="submit" variant="contained" className="w-full" disabled={!file}>
            {tCommon('uploadFile.upload')}
          </Button>

          <Box className="mt-3">
            <Button onClick={handleCancel} variant="outlined" className="w-full mt-3">
              {tCommon('buttons.cancel')}
            </Button>
          </Box>
        </Box>
      </Box>

      <ProductBatchResultDialog
        key={`stock-items-dialog-${!!stockItemsUploadResult}`}
        open={!!(stockItemsUploadResult || error)}
        onClose={handleCloseUploadResult}
        error={error}
        fileResult={stockItemsUploadResult}
        loading={loading}
      />
    </ThemeProvider>
  );
};
export default UploadStockItemsFile;
